import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  ChevronRight,
  ChevronDown,
  ListChecks,
  Layers,
  Calendar,
  Building2,
  ClipboardPaste,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTran } from "@/hooks";
import { useAuthStore } from "@/features/auth/hooks";
import { Can } from "@/hooks/useCan/Can";
import {
  useTargetSetupStore,
  useTargetSetupUIStore,
} from "../hooks/useTargetSetupStore";
import { loadDisclosureTargets } from "../operations/target-setup.ops";
import {
  buildDisclosureYearKey,
  isComputedReadOnly,
  resolveDisclosureId,
} from "../domains/targetSetup";
import { humanizeFormula } from "@/features/esg/utils/formula-display";
import ExpandedDisclosurePanel from "./ExpandedDisclosurePanel";
import { checkUserFromRole } from "@/helpers";
import { MetricCodeBadge } from "@/components/common";

const DisclosureTargetRow = observer(
  ({ disclosure, getCardyearLabel, getReportingPeriodMonths }) => {
    const { t } = useTran();
    const { user, userRole } = useAuthStore();
    const permissions = user?.permissions || [];
    const isUserLevel = checkUserFromRole(userRole);
    const hasPermission =
      !isUserLevel && permissions.includes("target_setting:update");

    const { reportingBoundary, filteredOrgTree, frameworks } =
      useTargetSetupStore();
    const { isExpanded, toggleExpandDisclosure, openSetValueToAllDialog } =
      useTargetSetupUIStore();

    const year =
      disclosure.masterDataYear ||
      disclosure.masterDataYears?.[disclosure.masterDataYears.length - 1] ||
      reportingBoundary?.endYear ||
      new Date().getFullYear();
    const disclosureId = resolveDisclosureId(disclosure);
    const disclosureUiKey = buildDisclosureYearKey(
      disclosureId || disclosure.code || disclosure.title,
      year,
    );
    const expanded = isExpanded(disclosureUiKey);
    const readOnly = isComputedReadOnly(disclosure);
    const monthOptions = useMemo(
      () => getReportingPeriodMonths?.(year),
      [getReportingPeriodMonths, year],
    );

    const handleToggle = () => {
      toggleExpandDisclosure(disclosureUiKey);
      if (!expanded && disclosureId) {
        loadDisclosureTargets(disclosureId, year);
      }
    };

    const handleSetValueToAll = (e) => {
      e.stopPropagation();
      openSetValueToAllDialog(disclosure);
    };

    const visibleOrganizations = (disclosure.organizations || []).slice(0, 3);
    const remainingCount = Math.max(
      0,
      (disclosure.organizations || []).length - visibleOrganizations.length,
    );
    const companyName =
      user?.companyName ||
      (typeof user?.company === "string"
        ? user.company
        : user?.company?.companyName || user?.company?.name) ||
      filteredOrgTree?.[0]?.companyName ||
      filteredOrgTree?.[0]?.name ||
      t("Parent Company");

    return (
      <div className="rounded-lg border border-border bg-card">
        {/* Row header */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(e) => e.key === "Enter" && handleToggle()}
          className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition cursor-pointer"
        >
          {/* Icon */}
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ListChecks className="h-5 w-5" />
          </span>

          {/* Info */}
          <div className="flex flex-1 flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <MetricCodeBadge
                code={disclosure.code}
                category={disclosure.category}
              />
              <span className="text-sm font-semibold text-foreground truncate">
                {disclosure.title}
              </span>
              {readOnly ? (
                <Badge
                  variant="outline"
                  className="text-[10px] text-muted-foreground shrink-0"
                >
                  {t("Computed")}
                </Badge>
              ) : null}
            </div>

            {readOnly && disclosure.formula ? (
              <span className="text-xs text-muted-foreground">
                {t("Calculated")}:{" "}
                {humanizeFormula(disclosure.formula, {
                  baselineYear: disclosure.baselineYear,
                })}
              </span>
            ) : null}

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Layers size={12} />
                {(() => {
                  const code = disclosure.framework || "JJ-Group";
                  const match = frameworks.find(
                    (fw) =>
                      (fw.code || fw.framework || fw.value || fw.id) === code,
                  );
                  return match?.name || match?.label || code;
                })()}
              </span>
              {reportingBoundary ? (
                <span className="flex items-center gap-1 text-primary font-medium">
                  <Calendar size={12} />
                  {getCardyearLabel(String(year))}
                </span>
              ) : null}
              {disclosure.isAllOrganization ? (
                <span className="flex items-center gap-1">
                  <Building2 size={12} />
                  {t("For all organization")}
                </span>
              ) : (
                <>
                  {disclosure.isCompanySelected && (
                    <span className="flex items-center gap-1 text-primary font-medium">
                      <Building2 size={12} />
                      {companyName}
                    </span>
                  )}
                  {visibleOrganizations.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Building2 size={12} />
                      {visibleOrganizations.join(", ")}
                      {remainingCount > 0 ? ` +${remainingCount}` : ""}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {!readOnly && hasPermission ? (
              <Can
                resource="target_setting"
                action="create"
                userPermissions={permissions}
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSetValueToAll}
                  className="gap-1.5 text-primary border-primary/30 hover:bg-primary/5"
                >
                  <ClipboardPaste size={14} />
                  {t("Set same value to all organizations")}
                </Button>
              </Can>
            ) : null}
            {expanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Expanded panel */}
        {expanded ? (
          <div className="border-t border-border">
            <ExpandedDisclosurePanel
              disclosure={disclosure}
              year={year}
              monthOptions={monthOptions}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

export default DisclosureTargetRow;
